import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  in_progress: "bg-blue-100 text-blue-700", staff_interview: "bg-indigo-100 text-indigo-700",
  staff_data_entry: "bg-indigo-100 text-indigo-700", waiting_on_client: "bg-amber-100 text-amber-700",
  submitted: "bg-violet-100 text-violet-700", reopened: "bg-orange-100 text-orange-700",
};

function Widget({ icon, label, value, tone }) {
  const Ic = Icons[icon];
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Ic className="h-5 w-5" /></div>
      <div><div className="text-2xl font-bold text-slate-900 leading-none">{value}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>
    </div>
  );
}

export default function OrganizerReviewQueue() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = new Set(user?.modules || []);
  const hasAdmin = allowed.has("administration");
  const hasClientSuccess = allowed.has("client_success");
  const showAdminTab = hasAdmin || hasClientSuccess;

  const [wsTab, setWsTab] = useState("review");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: "submitted" });
  const [selected, setSelected] = useState(new Set());
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: 1, page_size: 50 });
    if (q) params.set("q", q);
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const { data } = await api.get(`/ops/queues/organizer_review/items?${params}`);
    setData(data); setSelected(new Set());
  }, [q, filters]);
  useEffect(() => { load(); }, [load]);

  const def = data?.definition;
  const items = data?.items || [];
  const w = data?.widgets || {};

  const doAction = async (item, action, payload = {}) => {
    try {
      await api.post(`/ops/queues/organizer_review/items/${item.id}/action`, { action, ...payload });
      toast.success(`${action.replace(/_/g, " ")} done`); setDialog(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulkApprove = async () => {
    try { const { data } = await api.post(`/ops/queues/organizer_review/bulk`, { action: "approve", ids: [...selected] }); toast.success(data.message); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const allSel = items.length > 0 && items.every((i) => selected.has(i.id));

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="organizer-review-queue">
      {/* Workspace tab bar */}
      <div className="flex items-center gap-0.5 border-b border-slate-200 pb-0 -mb-1" data-testid="org-workspace-tabs">
        <button data-testid="ws-tab-review" onClick={() => setWsTab("review")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${wsTab === "review" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"}`}>
          <Icons.ClipboardCheck className="h-4 w-4" />Review
        </button>
        {showAdminTab && (
          <button data-testid="ws-tab-admin" onClick={() => setWsTab("admin")}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${wsTab === "admin" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"}`}>
            <Icons.Settings2 className="h-4 w-4" />Administration
          </button>
        )}
      </div>

      {/* Administration panel */}
      {wsTab === "admin" && (
        <div data-testid="org-admin-panel">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Organizer Administration</h2>
            <p className="text-sm text-slate-500 mt-0.5">Configuration tools for the organizer workflow.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {hasAdmin && (
              <button data-testid="org-admin-link-map" onClick={() => navigate("/organizer-map")}
                className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                    <Icons.GitBranch className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Organizer Map</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Administration</div>
                  </div>
                </div>
                <p className="text-sm text-slate-500">Configure service-to-organizer routing and mapping logic.</p>
              </button>
            )}
            {hasClientSuccess && (
              <button data-testid="org-admin-link-templates" onClick={() => navigate("/organizer-templates")}
                className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Icons.LayoutTemplate className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">Organizer Templates</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Client Success</div>
                  </div>
                </div>
                <p className="text-sm text-slate-500">Manage organizer templates used when generating client organizers.</p>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Review tab content */}
      {wsTab === "review" && <>
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">Operations</p>
        <h1 className="text-2xl font-bold text-slate-900">{def?.title || "Organizer Review Queue"}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{def?.description}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Widget icon="ClipboardCheck" label="Awaiting Review" value={w.awaiting_review ?? "—"} tone="bg-violet-100 text-violet-700" />
        <Widget icon="ClipboardPen" label="In Progress" value={w.in_progress ?? "—"} tone="bg-blue-100 text-blue-700" />
        <Widget icon="UserRoundSearch" label="Waiting on Client" value={w.waiting_on_client ?? "—"} tone="bg-amber-100 text-amber-700" />
        <Widget icon="CheckCircle2" label="Completed" value={w.completed ?? "—"} tone="bg-emerald-100 text-emerald-700" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="orq-search" placeholder="Search client or organizer…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        {def?.filters?.map((f) => (
          <select key={f.key} value={filters[f.key] || ""} onChange={(e) => setFilters((p) => ({ ...p, [f.key]: e.target.value }))}
            data-testid={`orq-filter-${f.key}`} className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 text-sm">
            <option value="">{f.label}: All</option>
            {f.options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="bg-blue-600 text-white rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="orq-bulk-bar">
          <span className="font-medium">{selected.size} selected</span>
          <button onClick={bulkApprove} data-testid="orq-bulk-approve" className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-1.5"><Icons.CheckCheck className="h-4 w-4" />Approve & Complete</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-white/80 hover:text-white">Clear</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="orq-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2.5"><input type="checkbox" checked={allSel} onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} data-testid="orq-select-all" /></th>
            {def?.columns?.map((c) => <th key={c.key} className="px-3 py-2.5 font-medium">{c.label}</th>)}
            <th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={20} className="px-5 py-12 text-center text-slate-400">🎉 Nothing to review. You're all caught up.</td></tr> :
            items.map((i) => (
              <tr key={i.id} data-testid={`orq-row-${i.id}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-3 py-2.5"><input type="checkbox" checked={selected.has(i.id)} onChange={(e) => setSelected((s) => { const n = new Set(s); e.target.checked ? n.add(i.id) : n.delete(i.id); return n; })} data-testid={`orq-select-${i.id}`} /></td>
                {def.columns.map((c) => (
                  <td key={c.key} className="px-3 py-2.5 text-slate-700">
                    {c.key === "status" ? <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[i[c.key]] || "bg-slate-100"}`}>{(i[c.key] || "").replace(/_/g, " ")}</span> :
                     c.key === "progress" ? `${i[c.key]}%` :
                     c.key === "completion_method" ? (i[c.key] || "—").replace(/_/g, " ") :
                     c.key === "organizer_name" ? <button onClick={() => navigate(`/client-success/organizers/${i.organizer_id}`)} className="text-blue-600 hover:underline">{i[c.key]}</button> :
                     <span className={c.key === "client_name" ? "font-medium text-slate-800" : ""}>{i[c.key] ?? "—"}</span>}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><button data-testid={`orq-menu-${i.id}`} className="p-1.5 rounded hover:bg-slate-100"><Icons.MoreHorizontal className="h-4 w-4 text-slate-500" /></button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/client-success/organizers/${i.organizer_id}`)} data-testid={`orq-open-${i.id}`}><Icons.ExternalLink className="h-4 w-4 mr-2" />Open Organizer</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => doAction(i, "approve")} data-testid={`orq-approve-${i.id}`}><Icons.Check className="h-4 w-4 mr-2" />Approve & Complete</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "request_clarification" })}><Icons.MessageCircleQuestion className="h-4 w-4 mr-2" />Request Clarification</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => doAction(i, "reopen")}><Icons.RotateCcw className="h-4 w-4 mr-2" />Reopen</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setDialog({ item: i, action: "add_note" })}><Icons.StickyNote className="h-4 w-4 mr-2" />Add Note</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dialog && <NoteDialog dialog={dialog} onClose={() => setDialog(null)} onConfirm={(text) => doAction(dialog.item, dialog.action, { note: text })} />}
      </>}
    </div>
  );
}

function NoteDialog({ dialog, onClose, onConfirm }) {
  const [text, setText] = useState("");
  const titles = { request_clarification: "Request Clarification", add_note: "Add Internal Note" };
  const required = dialog.action === "request_clarification";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="orq-note-dialog">
        <DialogHeader><DialogTitle>{titles[dialog.action]}</DialogTitle></DialogHeader>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="orq-note-text"
          placeholder={required ? "What do you need the client to clarify? (sent to their portal)" : "Internal note (staff only)…"}
          className="w-full border border-slate-200 rounded-lg p-2.5 text-sm" />
        <DialogFooter>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={() => { if (required && !text.trim()) return toast.error("A note is required."); onConfirm(text); }}
            data-testid="orq-note-confirm" className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium">Confirm</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
